import { useEffect, useId, useRef, useState } from 'react';

import { useCustomers, type Customer } from '@/api/masters';
import { focusAfter } from '@/components/enterKeyNavigation';
import { useDebounced } from '@/components/useDebounced';

/**
 * Find the customer who is paying, or create them without leaving the booking.
 *
 * <p>
 * <strong>This replaces a control that hard-coded customer id 1.</strong> The create-booking drawer
 * had a bare text input whose `onChange` set `customerId: value ? 1 : null` behind a comment saying
 * a real picker would resolve the id. Nothing downstream could tell the difference — `draftProblems`
 * only ever asked whether the id was null — so every booking taken through that screen was filed
 * against whichever customer happened to hold id 1, silently. That is why this is a fix rather than
 * a feature.
 * </p>
 *
 * <p>
 * <strong>The search is server-side, because the customer master is not a bounded list.</strong>
 * Every other picker in the product is an antd `Select` with `showSearch` filtering options already
 * in the browser, which is right for a list of branches or ID-document types and wrong here: a chain
 * with four shops and a few wedding seasons behind it carries thousands of customers, which is what
 * `useCustomers` is paged for. So this debounces, queries, and renders what came back.
 * </p>
 *
 * <p>
 * <strong>Creating is offered from inside the results, not beside them.</strong> The moment a clerk
 * knows they need a new customer is the moment the search comes back empty, so that is where the
 * affordance lives — and it carries whatever was typed across into the form, since a number that was
 * just typed and failed to match is exactly the number the new row wants.
 * </p>
 */
export function CustomerPicker({
  branchId,
  value,
  onSelect,
  onCreateRequested,
  disabled = false,
  autoFocus = false,
}: {
  branchId: number | undefined;
  /** The chosen customer, or null while none is chosen. The caller owns it. */
  value: Customer | null;
  onSelect: (customer: Customer | null) => void;
  /** Raised with whatever was typed, so the create form opens pre-filled. */
  onCreateRequested: (typed: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  /**
   * The list is open over a customer who is already chosen.
   *
   * <p>
   * Kept apart from `open` because the two answer different questions: `open` is whether the
   * dropdown is showing, `browsing` is whether the caller still holds a customer underneath it.
   * That is what lets backing out restore the card instead of emptying the field.
   * </p>
   */
  const [browsing, setBrowsing] = useState(false);

  const listId = useId();
  const box = useRef<HTMLDivElement>(null);

  // Where to carry on from once the chosen customer has replaced the search box. Held rather than
  // read at the time because the element focus is *in* is one of the things that goes away — see
  // the effect below.
  const resume = useRef<HTMLElement | null>(null);

  const debounced = useDebounced(query);

  // Ten, not twenty-five: this is a dropdown a clerk reads at a counter, not a page they scan.
  // Narrowing the search is faster than scrolling, and the count below says when to narrow it.
  const results = useCustomers(branchId, { search: debounced, pageSize: 10 });

  const matches = results.data?.items ?? [];
  const total = results.data?.total ?? 0;

  useEffect(() => {
    setActive(0);
  }, [debounced]);

  /**
   * Return to the chosen card without changing anything.
   *
   * <p>
   * <strong>This is what "Change" used to destroy.</strong> It cleared the customer outright, so
   * opening the list to check a second name and then thinking better of it left the field empty —
   * and on the booking counter that means re-finding the person you had already found, with
   * somebody standing at the counter. Backing out now keeps what was there; the only ways to lose a
   * customer are picking another one or saying so on the chosen row.
   * </p>
   */
  const backOut = () => {
    setQuery('');
    setOpen(false);
    setBrowsing(false);
  };

  // Click-outside closes. Pointerdown rather than click, so choosing a row inside still registers
  // before the panel goes — a click listener here races with the row's own handler.
  useEffect(() => {
    if (!open) {
      return;
    }

    const away = (event: PointerEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) {
        backOut();
      }
    };

    document.addEventListener('pointerdown', away);

    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const choose = (customer: Customer) => {
    // Picking the row that is already chosen means "not this one after all". The list is the only
    // place a choice can be undone, which is why the row says so rather than leaving the reader to
    // work out that clicking it again does something.
    if (customer.id === value?.id) {
      onSelect(null);
      backOut();

      return;
    }

    // Captured before the swap, because `box` is on the search panel and unmounts with it. The
    // parent — the `<label>` the caller wrapped this in — outlives both states of the picker, which
    // is what makes "the next field after this one" answerable a render later.
    resume.current = box.current?.parentElement ?? null;

    onSelect(customer);
    setQuery('');
    setOpen(false);
    setBrowsing(false);
  };

  /**
   * Carry on to the next field once a customer is chosen.
   *
   * <p>
   * Choosing replaces the search box with a card naming who was picked, so the control that had
   * focus stops existing and focus falls to `<body>` — after which Enter does nothing, Tab starts
   * from the top of the document, and a clerk who was typing has to reach for the mouse. On the
   * screen whose entire argument is that they never have to, that is the wrong ending.
   * </p>
   *
   * <p>
   * <strong>It runs on the pick, not on the value.</strong> The ref is set only inside `choose`, so
   * a caller restoring a saved draft or clearing the field does not have focus yanked out from
   * under whatever the user was doing.
   * </p>
   */
  useEffect(() => {
    const anchor = resume.current;

    if (!value || !anchor) {
      return;
    }

    resume.current = null;
    focusAfter(anchor);
  }, [value]);

  /* A customer is chosen: show who, not a search box they would have to clear to read. */
  if (value && !browsing) {
    return (
      <div className="border-border-color flex items-center gap-3 rounded border bg-white px-3 py-2">
        <span className="bg-primary-transparent text-primary flex size-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold">
          {initials(value.name)}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-gray-900">{value.name}</p>
          <p className="text-default font-mono-nums truncate text-[12px]">
            {value.mobile}
            {value.city ? ` · ${value.city}` : ''}
            {value.rentalCount > 0 ? ` · ${value.rentalCount} rentals` : ' · first booking'}
          </p>
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setBrowsing(true);
            setOpen(true);
          }}
          className="text-default shrink-0 cursor-pointer text-[12px] hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={box} className="relative">
      <div className="relative">
        <i className="icon icon-search text-default pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-[13px]" />
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          // Browsing is a deliberate press of "Change", so the cursor goes where the typing has to
          // happen. The input mounts at that moment, which is what makes the plain attribute enough.
          autoFocus={autoFocus || browsing}
          disabled={disabled}
          value={query}
          placeholder="Search by name or mobile"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActive((was) => Math.min(was + 1, matches.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((was) => Math.max(was - 1, 0));
            } else if (event.key === 'Enter') {
              // Swallowed so the page's Enter-to-next-field handler does not move focus out of a
              // list the clerk is choosing from. Same reason `enterKeyNavigation` exempts an open
              // dropdown — this one just reports it through `aria-expanded` rather than antd doing it.
              event.preventDefault();

              const picked = matches[active];

              if (picked) {
                choose(picked);
              } else if (query.trim()) {
                onCreateRequested(query.trim());
              }
            } else if (event.key === 'Escape') {
              backOut();
            }
          }}
          className="border-border-color h-9 w-full rounded border ps-8 pe-2.5 text-[13px] disabled:cursor-not-allowed disabled:bg-gray-50"
        />
      </div>

      {open && (
        <div
          id={listId}
          role="listbox"
          className="border-border-color absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded border bg-white shadow-lg"
        >
          {results.isLoading && <p className="text-default px-3 py-3 text-[12.5px]">Searching…</p>}

          {!results.isLoading && matches.length === 0 && (
            <p className="text-default px-3 py-3 text-[12.5px]">
              {debounced
                ? 'Nobody matches that. Try part of the number — it matches with or without spaces.'
                : 'Start typing a name or mobile number.'}
            </p>
          )}

          {matches.map((customer, index) => {
            const chosen = customer.id === value?.id;

            return (
              <button
                key={customer.id}
                type="button"
                role="option"
                /*
                  What is chosen, not what the arrow keys are over. Those are two different facts and
                  this attribute means the first: a screen reader announcing the highlighted row as
                  "selected" would tell a blind clerk they had already picked it. The keyboard
                  position is carried by the background, as it was.
                */
                aria-selected={chosen}
                title={chosen ? 'Chosen — click to remove' : undefined}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(customer)}
                className={`flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-start ${
                  index === active ? 'bg-light' : 'bg-white'
                }`}
              >
                <span className="bg-primary-transparent text-primary flex size-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold">
                  {initials(customer.name)}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-gray-900">
                    {customer.name}
                  </span>
                  <span className="text-default font-mono-nums block truncate text-[11.5px]">
                    {customer.mobile}
                    {customer.city ? ` · ${customer.city}` : ''}
                  </span>
                </span>

                {chosen ? (
                  /*
                    Says who the field is already holding, so reopening the list reads as "this one,
                    unless you say otherwise" rather than as an empty question. It takes the rental
                    count's place on that one row: the count is there to tell two people of the same
                    name apart, and on the row already chosen that question has been answered.
                  */
                  <span className="bg-primary-transparent text-primary shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium">
                    Chosen · remove
                  </span>
                ) : (
                  /* Rental history is the fastest way to tell two people with the same name apart,
                     and it is what a clerk would otherwise open another screen to check. */
                  <span className="text-default shrink-0 text-[11px]">
                    {customer.rentalCount > 0 ? `${customer.rentalCount} rentals` : 'new'}
                  </span>
                )}
              </button>
            );
          })}

          {total > matches.length && (
            <p className="text-default border-border-color border-t px-3 py-2 text-[11.5px]">
              {total - matches.length} more match — keep typing to narrow it.
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              onCreateRequested(query.trim());
              setOpen(false);
              setBrowsing(false);
            }}
            className="border-border-color text-primary flex w-full cursor-pointer items-center gap-2 border-t px-3 py-2.5 text-[12.5px] font-medium hover:bg-light"
          >
            <i className="icon icon-plus text-[13px]" />
            {query.trim() ? `Create “${query.trim()}” as a new customer` : 'Create a new customer'}
          </button>
        </div>
      )}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }

  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}
